/**
 * 이미지 트랜스코딩 호스트 — 결정 #18. 거대 PNG/JPEG를 WebP로 재인코딩해 저장 용량을
 * 줄인다. 무거운 디코드/인코딩은 Web Worker(OffscreenCanvas)로 오프로딩하고, 호스트는:
 *  - 변환 가치 판단(거대 raster만; WebP/GIF/SVG/AVIF/작은 파일은 건너뜀),
 *  - 결과가 실제로 더 작을 때만 채택(아니면 원본 유지),
 *  - Worker 미지원(Safari 15)/실패 시 원본 무손실 폴백
 * 을 담당한다. 어떤 경우에도 이미지를 잃지 않는다.
 */

/** 이 크기(바이트) 이상의 raster만 트랜스코딩 시도(작은 건 이득 < 비용). */
const MIN_TRANSCODE_BYTES = 512 * 1024; // 512KB
/** WebP 인코딩 품질. */
const WEBP_QUALITY = 0.85;
/** 변환본이 원본의 이 비율보다 작아야 채택(미미한 이득은 원본 유지). */
const ACCEPT_RATIO = 0.9;

/** 트랜스코딩 대상 MIME(손실 재인코딩이 안전하고 이득이 큰 raster만). */
const TRANSCODABLE = new Set(["image/png", "image/jpeg", "image/jpg"]);

interface PendingResolver {
  resolve: (r: { bytes: ArrayBuffer; mime: string } | null) => void;
}

export class ImageTranscodeHost {
  private worker: Worker | null = null;
  private workerFailed = false;
  private seq = 0;
  private readonly pending = new Map<number, PendingResolver>();

  /** OffscreenCanvas 지원 여부(Safari 15는 미지원 → 트랜스코딩 비활성). */
  private supported(): boolean {
    return (
      typeof Worker !== "undefined" &&
      typeof OffscreenCanvas !== "undefined" &&
      typeof createImageBitmap === "function"
    );
  }

  private getWorker(): Worker | null {
    if (this.worker || this.workerFailed) return this.worker;
    try {
      this.worker = new Worker(new URL("./transcode-worker.ts", import.meta.url), {
        type: "module",
      });
      this.worker.onmessage = (
        e: MessageEvent<{ id: number; ok: boolean; bytes?: ArrayBuffer; mime?: string }>,
      ) => {
        const { id, ok, bytes, mime } = e.data;
        const p = this.pending.get(id);
        if (!p) return;
        this.pending.delete(id);
        p.resolve(ok && bytes && mime ? { bytes, mime } : null);
      };
      this.worker.onerror = () => {
        this.workerFailed = true;
        for (const [, p] of this.pending) p.resolve(null);
        this.pending.clear();
        this.worker?.terminate();
        this.worker = null;
      };
    } catch {
      this.workerFailed = true;
      this.worker = null;
    }
    return this.worker;
  }

  /**
   * Blob을 트랜스코딩해 더 작은 결과를 돌려준다. 변환이 무의미하거나(작은 파일/비대상
   * 포맷) 실패/미지원이면 **원본 그대로** 돌려준다(절대 손실 없음).
   */
  async maybeTranscode(blob: Blob): Promise<Blob> {
    const mime = blob.type.toLowerCase();
    // 대상 포맷 + 충분히 큰 것만. 그 외는 즉시 원본.
    if (!TRANSCODABLE.has(mime) || blob.size < MIN_TRANSCODE_BYTES || !this.supported()) {
      return blob;
    }
    const worker = this.getWorker();
    if (!worker) return blob;

    const id = ++this.seq;
    const bytes = await blob.arrayBuffer();
    const result = await new Promise<{ bytes: ArrayBuffer; mime: string } | null>((resolve) => {
      this.pending.set(id, { resolve });
      // ArrayBuffer를 transfer(복사 0). 이후 이 blob의 arrayBuffer는 재사용 안 함.
      worker.postMessage({ id, bytes, mime, quality: WEBP_QUALITY }, [bytes]);
    });

    if (!result) return blob; // 실패/미지원 → 원본
    // 충분히 작아졌을 때만 채택(미미하면 원본 유지로 단순성·호환성 우선).
    if (result.bytes.byteLength >= blob.size * ACCEPT_RATIO) return blob;
    return new Blob([result.bytes], { type: result.mime });
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.pending.clear();
  }
}

/** 앱 전역 트랜스코딩 호스트(결정 #18). */
export const imageTranscodeHost = new ImageTranscodeHost();
